import { useTranslation } from 'react-i18next';

import type { SkillRecord } from '../types';

import { useTheme } from '@mui/material/styles';
import {
  Box,
  Stack,
  Button,
  Dialog,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
} from '@mui/material';

import { Monitor } from '../../components/lucide-icon';

import { AriMark } from './workflow/ari-chat-entries';

// The editor needs the room of a computer or tablet. An upright tablet is about this wide.
const EDITOR_MIN_WIDTH = 768;

/** Whether the skill editor fits this screen. Below EDITOR_MIN_WIDTH it is not offered. */
export function useEditorFits(): boolean {
  // Read on the first render, so a page that opens the editor knows at once.
  return !useMediaQuery(useTheme().breakpoints.down(EDITOR_MIN_WIDTH), { noSsr: true });
}

/** Newest skills first, the order every skills list uses. */
export function newestFirst<T extends { created_at: string }>(skills: readonly T[]): T[] {
  return [...skills].sort((a, b) => (Date.parse(b.created_at) || 0) - (Date.parse(a.created_at) || 0));
}

function Message() {
  const { t } = useTranslation();
  return (
    <Stack spacing={1.5} alignItems="center" sx={{ textAlign: 'center' }}>
      <AriMark size={56} icon={Monitor} />
      <Typography variant="h6" sx={{ fontWeight: 'fontWeightSemiBold' }}>
        {t('skills.bigScreen.title')}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
        {t('skills.bigScreen.body')}
      </Typography>
    </Stack>
  );
}

/** Shown on a phone in place of the skill editor, which needs the room of a computer or tablet. */
export function SkillsNeedBigScreen({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation();
  return (
    <Box
      data-testid="skills.big-screen"
      sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', px: 3, py: 4, bgcolor: 'background.default' }}
    >
      <Stack spacing={3} alignItems="center" sx={{ maxWidth: 360 }}>
        <Message />
        <Button variant="contained" onClick={onBack} data-testid="skills.big-screen.back">
          {t('skills.bigScreen.back')}
        </Button>
      </Stack>
    </Box>
  );
}

/** The same message as a dialog, for Create Skill on a phone, so no empty skill is made. */
export function SkillsNeedBigScreenDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" data-testid="skills.big-screen.dialog">
      <DialogContent sx={{ pt: 4 }}>
        <Message />
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button variant="contained" onClick={onClose}>
          {t('skills.bigScreen.ok')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface SkillSwitchBlockedDialogProps {
  /** The skill that was switched on although it is not published. Null keeps the dialog closed. */
  skill: SkillRecord | null;
  onClose: () => void;
  /** Opens the skill in the editor. Left out where the editor cannot open. */
  onOpen?: (skill: SkillRecord) => void;
}

/**
 * Says why an unpublished skill cannot be switched on. Publishing happens in the editor, so
 * where the editor fits it offers to open the skill, and on a phone it only explains.
 */
export function SkillSwitchBlockedDialog({ skill, onClose, onOpen }: SkillSwitchBlockedDialogProps) {
  const { t } = useTranslation();
  const fits = useEditorFits();
  const error = !!skill?.has_errors;
  const body = fits
    ? error
      ? 'skills.switchBlocked.errorBody'
      : 'skills.switchBlocked.body'
    : error
    ? 'skills.switchBlocked.phoneErrorBody'
    : 'skills.switchBlocked.phoneBody';
  return (
    <Dialog open={skill !== null} onClose={onClose} fullWidth maxWidth="xs" data-testid="skills.switch-blocked">
      <DialogTitle>{t(error ? 'skills.switchBlocked.errorTitle' : 'skills.switchBlocked.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" data-testid="skills.switch-blocked.body">
          {t(body)}
        </Typography>
      </DialogContent>
      <DialogActions>
        {fits && onOpen ? (
          <>
            <Button onClick={onClose}>{t('buttons.cancel')}</Button>
            <Button
              variant="contained"
              data-testid="skills.switch-blocked.open"
              onClick={() => {
                if (skill) onOpen(skill);
                onClose();
              }}
            >
              {t('skills.switchBlocked.open')}
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={onClose}>
            {t('skills.switchBlocked.ok')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
